import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Spinner, Alert, InputGroup, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { FiArrowLeft, FiSearch, FiFilter, FiPlus, FiTrash2, FiUser, FiBriefcase, FiAlertCircle, FiMaximize, FiMail } from 'react-icons/fi';
import api from '../services/api';

const TrainingParticipants = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [training, setTraining] = useState(null);
    const [allParticipants, setAllParticipants] = useState([]);
    const [assignedParticipants, setAssignedParticipants] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [notifyLoading, setNotifyLoading] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [isConcluded, setIsConcluded] = useState(false);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDept, setFilterDept] = useState('');
    
    const [assignedSearchTerm, setAssignedSearchTerm] = useState('');
    const [assignedFilterDept, setAssignedFilterDept] = useState('');

    
    const [showQrModal, setShowQrModal] = useState(false);
    const attendanceUrl = training?.qr_code_token ? `${window.location.origin}/attendance/${training.qr_code_token}` : '';
    
    const [allowAddAfterStart, setAllowAddAfterStart] = useState(false);

    useEffect(() => {
        loadData();
    }, [id]);

    useEffect(() => {
        if (training) {
            const startDate = new Date(training.data);
            
            // isExpired should mean "isStarted" in this context?
            // The user said: "o botão de adicionar participante ao treinamento não está ficando desabilitado após iniciar um treinamento"
            // So we need to check if the training HAS STARTED.
            // Previous logic: setIsExpired(new Date() > endDate); -> This checks if it FINISHED.
            
            // Correct Logic: Check if NOW > Start Date
            const now = new Date();
            const hasStarted = now >= startDate;
            
            setIsExpired(hasStarted);
            setIsConcluded(training.status === 'Concluído');
        }
    }, [training]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [trainingRes, allRes, assignedRes, settingsRes] = await Promise.all([
                api.get(`/trainings/${id}`),
                api.get('/participants'),
                api.get(`/trainings/${id}/participants`),
                api.get('/settings')
            ]);
            
            setTraining(trainingRes.data);
            setAllParticipants(allRes.data);
            setAssignedParticipants(assignedRes.data);
            setAllowAddAfterStart(settingsRes.data.allow_add_participant_after_start);
        } catch (err) {
            setError('Erro ao carregar dados. Tente novamente.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleNotify = async () => {
        if (!window.confirm("Deseja enviar e-mail de notificação para todos os participantes inscritos?")) return;
        
        setNotifyLoading(true);
        setSuccess('');
        setError('');
        
        try {
            const response = await api.post(`/trainings/${id}/notify`);
            setSuccess(response.data.msg);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.msg || 'Erro ao enviar notificações.');
        } finally {
            setNotifyLoading(false);
        }
    };

    const handleAdd = async (participantId) => {
        setActionLoading(true);
        setError('');
        try {
            await api.post(`/trainings/${id}/participants`, { participant_ids: [participantId] });
            // Refresh assigned list or optimistic update
            const participant = allParticipants.find(p => p.id === participantId);
            setAssignedParticipants([...assignedParticipants, participant]);
            setSuccess('Participante adicionado com sucesso!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao adicionar participante.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddAll = async () => {
        if (availableParticipants.length === 0) return;
        
        setActionLoading(true);
        setError('');
        try {
            const participantIds = availableParticipants.map(p => p.id);
            await api.post(`/trainings/${id}/participants`, { participant_ids: participantIds });
            
            setAssignedParticipants([...assignedParticipants, ...availableParticipants]);
            setSuccess(`${availableParticipants.length} participante(s) adicionado(s) com sucesso!`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao adicionar participantes.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveAll = async () => {
        if (filteredAssignedParticipants.length === 0) return;
        if (!window.confirm(`Tem certeza que deseja remover ${filteredAssignedParticipants.length} participante(s) listado(s)?`)) return;
        
        setActionLoading(true);
        setError('');
        try {
            const participantIds = filteredAssignedParticipants.map(p => p.id);
            await api.delete(`/trainings/${id}/participants`, { data: { participant_ids: participantIds } });
            
            const idsToRemove = new Set(participantIds);
            setAssignedParticipants(assignedParticipants.filter(p => !idsToRemove.has(p.id)));
            setSuccess(`${participantIds.length} participante(s) removido(s) com sucesso!`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao remover participantes.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemove = async (participantId) => {
        if (!window.confirm('Tem certeza que deseja remover este participante do treinamento?')) return;
        
        setActionLoading(true);
        setError('');
        try {
            await api.delete(`/trainings/${id}/participants/${participantId}`);
            setAssignedParticipants(assignedParticipants.filter(p => p.id !== participantId));
            setSuccess('Participante removido com sucesso!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao remover participante.');
        } finally {
            setActionLoading(false);
        }
    };

    // Filter logic
    const assignedIds = new Set(assignedParticipants.map(p => p.id));
    
    // Get unique departments for filter
    const departments = [...new Set(allParticipants
        .map(p => p.departamento)
        .filter(d => d) // Remove null/empty
    )].sort();

    const availableParticipants = allParticipants.filter(p => {
        // Exclude already assigned
        if (assignedIds.has(p.id)) return false;
        
        // Apply Department Filter
        if (filterDept && p.departamento !== filterDept) return false;
        
        // Apply Search Filter (Name)
        if (searchTerm && !p.nome.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        
        // Only active participants
        if (!p.ativo) return false;

        return true;
    }).sort((a, b) => a.nome.localeCompare(b.nome));

    const filteredAssignedParticipants = assignedParticipants.filter(p => {
        if (assignedFilterDept && p.departamento !== assignedFilterDept) return false;
        if (assignedSearchTerm && !p.nome.toLowerCase().includes(assignedSearchTerm.toLowerCase())) return false;
        return true;
    });

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (!training) return <Container className="mt-5"><Alert variant="danger">Treinamento não encontrado.</Alert></Container>;

    return (
        <Container className="py-4">
            <div className="d-flex align-items-center mb-4">
                <Button variant="link" as={Link} to="/training-list" className="text-decoration-none p-0 mb-2 text-muted">
                    <FiArrowLeft /> Voltar para Lista de treinamento
                </Button>
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    {training.titulo}
                </h2>
            
                {training.qr_code_token && (
                    <div className="d-flex gap-2">
                        {isConcluded ? (
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip>Notificações não estão disponíveis para treinamentos concluídos.</Tooltip>}
                            >
                                <span className="d-inline-block">
                                    <Button 
                                        variant="outline-primary" 
                                        disabled 
                                        style={{ pointerEvents: 'none', opacity: 0.6, cursor: 'not-allowed' }}
                                    >
                                        <FiMail className="me-2" /> Notificar
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        ) : (
                            <Button variant="outline-primary" onClick={handleNotify} disabled={notifying || assignedParticipants.length === 0}>
                                {notifying ? <Spinner animation="border" size="sm" /> : <FiMail className="me-2" />}
                                Notificar
                            </Button>
                        )}
                        <Button variant="outline-dark" onClick={() => setShowQrModal(true)}>
                            <FiMaximize className="me-2" /> QR Code Presença
                        </Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

            {isConcluded && (
                <Alert variant="info" className="mb-4 shadow-sm border-info">
                    <div className="d-flex align-items-center">
                        <FiAlertCircle className="fs-4 me-3" />
                        <div>
                            <strong>Treinamento Concluído</strong>
                            <p className="mb-0 small">Este treinamento foi marcado como concluído. A gestão de participantes e notificações está desabilitada.</p>
                        </div>
                    </div>
                </Alert>
            )}

            {!isConcluded && isExpired && !allowAddAfterStart && (
                <Alert variant="warning" className="mb-4 shadow-sm border-warning">
                    <div className="d-flex align-items-center">
                        <FiAlertCircle className="fs-4 me-3" />
                        <div>
                            <strong>Treinamento Iniciado</strong>
                            <p className="mb-0 small">A configuração do sistema não permite adicionar participantes com o teinamento iniciado.</p>
                        </div>
                    </div>
                </Alert>
            )}

            <Row>
                {/* Left Column: Available Participants */}
                <Col lg={6} className="mb-4">
                    <Card className="shadow-sm h-100 border-0">
                        <Card.Header className="bg-white py-3 border-bottom-0">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 fw-bold text-primary">Disponíveis</h5>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={handleAddAll}
                                    disabled={actionLoading || availableParticipants.length === 0 || isConcluded || (isExpired && !allowAddAfterStart)}
                                    style={(isConcluded || (isExpired && !allowAddAfterStart)) ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                    title={
                                        isConcluded 
                                        ? "Treinamento concluído" 
                                        : (isExpired && !allowAddAfterStart 
                                            ? "As configurações não permitem adicionar participante com o treinamento iniciado" 
                                            : "Adicionar todos os participantes disponíveis")
                                    }
                                >
                                    <FiPlus className="me-1" /> Todos
                                </Button>
                            </div>
                            
                            <Row className="g-2">
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-end-0"><FiSearch /></InputGroup.Text>
                                        <Form.Control 
                                            placeholder="Buscar por nome..." 
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="border-start-0"
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-end-0"><FiFilter /></InputGroup.Text>
                                        <Form.Select 
                                            value={filterDept} 
                                            onChange={(e) => setFilterDept(e.target.value)}
                                            className="border-start-0"
                                        >
                                            <option value="">Todos Departamentos</option>
                                            {departments.map(dept => (
                                                <option key={dept} value={dept}>{dept}</option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body className="p-0 overflow-auto" style={{ maxHeight: '500px' }}>
                            {availableParticipants.length === 0 ? (
                                <div className="text-center p-4 text-muted">
                                    Nenhum participante encontrado.
                                </div>
                            ) : (
                                <div className="list-group list-group-flush">
                                    {availableParticipants.map(p => (
                                        <div key={p.id} className="list-group-item p-3 d-flex justify-content-between align-items-center hover-bg-light">
                                            <div>
                                                <div className="fw-bold mb-1"><FiUser className="me-2 text-muted"/>{p.nome}</div>
                                                <div className="small text-muted mb-1">
                                                    <FiBriefcase className="me-1"/> {p.departamento || 'Sem departamento'}
                                                </div>
                                                <Badge bg="light" text="dark" className="border">{p.cargo}</Badge>
                                            </div>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                onClick={() => handleAdd(p.id)}
                                                disabled={actionLoading || isConcluded || (isExpired && !allowAddAfterStart)}
                                                style={(isConcluded || (isExpired && !allowAddAfterStart)) ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                                title={
                                                    isConcluded 
                                                    ? "Treinamento concluído" 
                                                    : (isExpired && !allowAddAfterStart 
                                                        ? "As configurações não permitem adicionar participante com o treinamento iniciado" 
                                                        : "Adicionar participante")
                                                }
                                            >                                                
                                                <FiPlus /> Adicionar
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column: Assigned Participants */}
                <Col lg={6} className="mb-4">
                    <Card className="shadow-sm h-100 border-0 bg-light">
                        <Card.Header className="bg-white py-3 border-bottom-0">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 fw-bold text-success">Adicionados ({assignedParticipants.length})</h5>
                                <Button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    onClick={handleRemoveAll}
                                    disabled={actionLoading || filteredAssignedParticipants.length === 0 || isConcluded}
                                    style={isConcluded ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                    title={isConcluded ? "Treinamento concluído" : "Remover todos listados"}
                                >
                                    <FiTrash2 className="me-1" /> Todos
                                </Button>
                            </div>
                            <Row className="g-2">
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-end-0"><FiSearch /></InputGroup.Text>
                                        <Form.Control 
                                            placeholder="Buscar por nome..." 
                                            value={assignedSearchTerm}
                                            onChange={(e) => setAssignedSearchTerm(e.target.value)}
                                            className="border-start-0"
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-end-0"><FiFilter /></InputGroup.Text>
                                        <Form.Select 
                                            value={assignedFilterDept} 
                                            onChange={(e) => setAssignedFilterDept(e.target.value)}
                                            className="border-start-0"
                                        >
                                            <option value="">Todos Departamentos</option>
                                            {departments.map(dept => (
                                                <option key={dept} value={dept}>{dept}</option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body className="p-0 overflow-auto" style={{ maxHeight: '500px' }}>
                            {filteredAssignedParticipants.length === 0 ? (
                                <div className="text-center p-5 text-muted">
                                    Nenhum participante adicionado a este treinamento.
                                </div>
                            ) : (
                                <div className="list-group list-group-flush">
                                    {filteredAssignedParticipants.slice().reverse().map(p => (
                                        <div key={p.id} className="list-group-item p-3 d-flex justify-content-between align-items-center bg-white mb-2 mx-3 rounded border shadow-sm">
                                            <div>
                                                <div className="fw-bold mb-1">{p.nome}</div>
                                                <div className="small text-muted">
                                                    {p.departamento} • {p.cargo}
                                                </div>
                                            </div>
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                onClick={() => handleRemove(p.id)}
                                                disabled={actionLoading || isConcluded}
                                                style={isConcluded ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                                className="rounded-circle p-2"
                                                title={isConcluded ? "Treinamento concluído" : "Remover"}
                                            >
                                                <FiTrash2 />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Registro de Presença</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-3">Escaneie para registrar presença:</p>
                    <div className="p-3 bg-white d-inline-block border rounded mb-3">
                        <QRCodeCanvas value={attendanceUrl} size={256} />
                    </div>
                    <div className="d-grid gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => navigator.clipboard.writeText(attendanceUrl)}>
                            Copiar Link
                        </Button>
                        <a href={attendanceUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                            Abrir Link
                        </a>
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default TrainingParticipants;
